import React, { useState, useEffect } from "react";
import { DatePicker, Select } from "antd";
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import { DataTable, PageCard, PageShell, PageToolbar } from "../../components/ui";

const { RangePicker } = DatePicker;
const { Option } = Select;

const PartnersReport = () => {
    const { t } = useTranslation();
    const [dateRange, setDateRange] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState("all");
    const [data, setData] = useState([]);
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);

    // Fetch partners
    const fetchPartners = async () => {
        try {
            const response = await axiosInstance.get("partners");
            setPartners(response.data.data);
        } catch (error) {
            console.error("Error fetching partners:", error);
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        if (!dateRange || !dateRange.length) return;
        setLoading(true);
        try {
            const [start, end] = dateRange;
            const response = await axiosInstance.get("admin/statistics/get", {
                params: {
                    start: start?.format("YYYY-MM-DD"),
                    end: end?.format("YYYY-MM-DD"),
                },
            });
            const stats = response.data;

            // Transform response into table data format
            const formattedData = Object.entries(stats).map(([partner, values], index) => ({
                key: index.toString(),
                partner,
                today: values.all, // Assuming "all" represents today's total patients
                newPatients: values.new,
                repeatPatients: values.old,
                percentage: values.protsent.toFixed(2), // Format to two decimals
            }));

            setData(formattedData);
        } catch (error) {
            console.error("Error fetching statistics:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPartners();
    }, []);

    useEffect(() => {
        fetchStatistics();
    }, [dateRange]);

    const handleDateChange = (dates) => setDateRange(dates);
    const handlePartnerChange = (value) => setSelectedPartner(value);

    const filteredData =
        selectedPartner === "all"
            ? data
            : data.filter((record) => record.partner === selectedPartner);

    const columns = [
        {
            title: t('reports.partner'),
            dataIndex: "partner",
            key: "partner",
            ellipsis: true,
            render: (text) => <span className="text-sm font-medium text-[#1D2C7B]">{text}</span>,
        },
        {
            title: t('reports.todayCame'),
            dataIndex: "today",
            key: "today",
            width: 160,
            align: "right",
            render: (text) => <span className="text-sm text-slate-700 tabular-nums">{text}</span>,
        },
        {
            title: t('reports.newPatients'),
            dataIndex: "newPatients",
            key: "newPatients",
            width: 170,
            align: "right",
            render: (text) => <span className="text-sm text-slate-700 tabular-nums">{text}</span>,
        },
        {
            title: t('reports.repeatPatients'),
            dataIndex: "repeatPatients",
            key: "repeatPatients",
            width: 190,
            align: "right",
            render: (text) => <span className="text-sm text-slate-700 tabular-nums">{text}</span>,
        },
        {
            title: t('reports.share'),
            dataIndex: "percentage",
            key: "percentage",
            width: 120,
            align: "right",
            render: (text) => (
                <span className="text-sm font-medium text-slate-900 tabular-nums">{text}</span>
            ),
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 truncate text-sm font-semibold text-[#1D2C7B]">
                    {record.partner}
                </span>
                <span className="shrink-0 text-sm font-semibold text-slate-900 tabular-nums">
                    {record.percentage}%
                </span>
            </div>

            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('reports.todayCame')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">{record.today}</span>
                </div>
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('reports.newPatients')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">{record.newPatients}</span>
                </div>
                <div className="min-w-0">
                    <span className="block text-[11px] leading-tight text-slate-500">{t('reports.repeatPatients')}</span>
                    <span className="block text-sm text-slate-700 tabular-nums">{record.repeatPatients}</span>
                </div>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('reports.partners')}
                    count={filteredData.length}
                    filters={
                        <>
                            <RangePicker
                                size="small"
                                value={dateRange}
                                onChange={handleDateChange}
                                className="w-full md:!w-[250px]"
                            />
                            <Select
                                size="small"
                                value={selectedPartner}
                                onChange={handlePartnerChange}
                                className="w-full md:!w-[200px]"
                            >
                                <Option value="all">{t('reports.allPartners')}</Option>
                                {partners.map((partner) => (
                                    <Option key={partner.id} value={partner.name}>
                                        {partner.name}
                                    </Option>
                                ))}
                            </Select>
                        </>
                    }
                />

                <DataTable
                    columns={columns}
                    dataSource={filteredData}
                    mobileCard={renderMobileCard}
                    scrollX={960}
                    loading={loading}
                    rowKey="key"
                    pagination={{
                        pageSize: 5,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                />
            </PageCard>

            {/* Pie Chart Placeholder */}
            <PageCard>
                <PageToolbar title={t('reports.partnersShare')} />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 p-2.5 sm:p-3">
                    {filteredData.map((record) => (
                        <PageCard key={record.key} className="px-3 py-2.5">
                            <span className="block truncate text-xs text-slate-500">{record.partner}</span>
                            <span className="mt-0.5 block text-lg font-semibold text-slate-900 tabular-nums">
                                {record.percentage}%
                            </span>
                        </PageCard>
                    ))}
                </div>
            </PageCard>
        </PageShell>
    );
};

export default PartnersReport;
